"use client"

import * as React from "react"
import type {
  GridViewContextValue,
  GridItemContextValue,
  GridItemData,
  ResponsiveColumns,
} from "./types"
import { DEFAULT_COLUMNS, DEFAULT_MIN_COLUMN_WIDTH, getGapInPixels } from "./constants"

// ============================================================================
// Contexts
// ============================================================================

export const GridViewContext = React.createContext<GridViewContextValue | null>(null)
export const GridItemContext = React.createContext<GridItemContextValue | null>(null)

// ============================================================================
// Primary Hooks (throw if not in context)
// ============================================================================

export function useGridView<
  TData extends GridItemData = GridItemData
>(): GridViewContextValue<TData> {
  const context = React.useContext(GridViewContext)
  if (!context) {
    throw new Error("useGridView must be used within a GridView component")
  }
  return context as unknown as GridViewContextValue<TData>
}

export function useGridItem<
  TData extends GridItemData = GridItemData
>(): GridItemContextValue<TData> {
  const context = React.useContext(GridItemContext)
  if (!context) {
    throw new Error("useGridItem must be used within a GridItem component")
  }
  return context as GridItemContextValue<TData>
}

// ============================================================================
// Optional Hooks (don't throw if not in context)
// ============================================================================

export function useGridViewOptional<
  TData extends GridItemData = GridItemData
>(): GridViewContextValue<TData> | null {
  return React.useContext(GridViewContext) as GridViewContextValue<TData> | null
}

export function useGridItemOptional<
  TData extends GridItemData = GridItemData
>(): GridItemContextValue<TData> | null {
  return React.useContext(GridItemContext) as GridItemContextValue<TData> | null
}

// ============================================================================
// Utility Hooks
// ============================================================================

/**
 * Hook to observe container width using ResizeObserver
 */
export function useContainerWidth(
  containerRef: React.RefObject<HTMLElement | null>
): number {
  const [width, setWidth] = React.useState(0)

  React.useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0]
      if (entry) {
        setWidth(entry.contentRect.width)
      }
    })

    observer.observe(container)
    setWidth(container.clientWidth)

    return () => observer.disconnect()
  }, [containerRef])

  return width
}

/**
 * Hook to calculate responsive column count based on container width
 */
export function useResponsiveColumns(
  containerWidth: number,
  columns: number | ResponsiveColumns | undefined,
  minColumnWidth: number = DEFAULT_MIN_COLUMN_WIDTH,
  gap: number = 4
): number {
  return React.useMemo(() => {
    // If no width yet, return a default
    if (containerWidth === 0) {
      if (typeof columns === "number") return columns
      return columns?.default ?? columns?.md ?? DEFAULT_COLUMNS.md ?? 3
    }

    // If explicit number, use it
    if (typeof columns === "number") {
      return columns
    }

    // If responsive config provided, use breakpoints
    if (columns) {
      if (containerWidth >= 1536 && columns["2xl"]) return columns["2xl"]
      if (containerWidth >= 1280 && columns.xl) return columns.xl
      if (containerWidth >= 1024 && columns.lg) return columns.lg
      if (containerWidth >= 768 && columns.md) return columns.md
      if (containerWidth >= 640 && columns.sm) return columns.sm
      if (columns.xs) return columns.xs
      if (columns.default) return columns.default
      return 1
    }

    // Auto-calculate based on minColumnWidth
    const gapPx = getGapInPixels(gap)
    const calculated = Math.floor((containerWidth + gapPx) / (minColumnWidth + gapPx))
    return Math.max(1, calculated)
  }, [containerWidth, columns, minColumnWidth, gap])
}

/**
 * Hook for infinite scroll detection using Intersection Observer
 */
export function useInfiniteScroll(
  sentinelRef: React.RefObject<HTMLElement | null>,
  options: {
    enabled: boolean
    hasNextPage: boolean
    isFetchingNextPage: boolean
    threshold: number
    onLoadMore?: () => void
  }
) {
  const { enabled, hasNextPage, isFetchingNextPage, threshold, onLoadMore } = options

  React.useEffect(() => {
    if (!enabled || !hasNextPage || isFetchingNextPage || !sentinelRef.current) {
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries
        if (entry?.isIntersecting) {
          onLoadMore?.()
        }
      },
      {
        rootMargin: `${threshold}px`,
        threshold: 0,
      }
    )

    observer.observe(sentinelRef.current)

    return () => observer.disconnect()
  }, [enabled, hasNextPage, isFetchingNextPage, threshold, onLoadMore, sentinelRef])
}

/**
 * Hook for scroll-based infinite scroll detection
 */
export function useScrollInfiniteScroll(
  containerRef: React.RefObject<HTMLElement | null>,
  options: {
    enabled: boolean
    hasNextPage: boolean
    isFetchingNextPage: boolean
    threshold: number
    onLoadMore?: () => void
  }
) {
  const { enabled, hasNextPage, isFetchingNextPage, threshold, onLoadMore } = options

  React.useEffect(() => {
    if (!enabled || !containerRef.current) return

    const container = containerRef.current

    const handleScroll = () => {
      if (!hasNextPage || isFetchingNextPage) return

      const { scrollTop, scrollHeight, clientHeight } = container
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight

      if (distanceFromBottom < threshold) {
        onLoadMore?.()
      }
    }

    container.addEventListener("scroll", handleScroll, { passive: true })
    return () => container.removeEventListener("scroll", handleScroll)
  }, [enabled, hasNextPage, isFetchingNextPage, threshold, onLoadMore, containerRef])
}

/**
 * Hook to manage measured heights cache
 */
export function useMeasuredHeights() {
  const [heights, setHeights] = React.useState<Map<string, number>>(
    () => new Map()
  )

  const reportHeight = React.useCallback((key: string, height: number) => {
    setHeights((prev) => {
      const existing = prev.get(key)
      if (existing === height) return prev
      const next = new Map(prev)
      next.set(key, height)
      return next
    })
  }, [])

  const clearHeights = React.useCallback(() => {
    setHeights(new Map())
  }, [])

  const getHeight = React.useCallback(
    (key: string): number | undefined => {
      return heights.get(key)
    },
    [heights]
  )

  return {
    heights,
    reportHeight,
    clearHeights,
    getHeight,
  }
}

/**
 * Hook to observe element height using ResizeObserver
 */
export function useElementHeight(
  elementRef: React.RefObject<HTMLElement | null>,
  onHeightChange?: (height: number) => void
): number {
  const [height, setHeight] = React.useState(0)

  React.useEffect(() => {
    const element = elementRef.current
    if (!element) return

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0]
      if (entry) {
        const newHeight = entry.contentRect.height
        setHeight(newHeight)
        onHeightChange?.(newHeight)
      }
    })

    observer.observe(element)
    setHeight(element.offsetHeight)

    return () => observer.disconnect()
  }, [elementRef, onHeightChange])

  return height
}
